import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Save,
  Loader2,
  Building2,
  Users,
  Link2,
  Copy,
  RefreshCw,
  Ban,
  Trash2,
  LogOut,
  KeyRound,
  UserCircle,
  Languages,
  AlertTriangle,
  DoorOpen,
} from "lucide-react";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/lib/i18n";

const retentionOptions = [
  { value: "never", labelKey: "keep_all_records", descKey: "keep_all_records_desc" },
  { value: "week", labelKey: "auto_delete_weekly", descKey: "auto_delete_weekly_desc" },
  { value: "month", labelKey: "auto_delete_monthly", descKey: "auto_delete_monthly_desc" },
  { value: "custom", labelKey: "custom", descKey: "custom_desc" },
];

const SectionLabel = ({ icon: Icon, children }) => (
  <div className="mb-4 flex items-center gap-2">
    {Icon && <Icon className="h-4 w-4 text-primary" />}
    <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground">{children}</p>
  </div>
);

export default function Settings() {
  const t = useT();
  const { user, account, setAccount, updateProfile, updateLanguage, logout } = useAuth();

  const changeLanguage = async (lang) => {
    try {
      await updateLanguage(lang);
      toast.success("Language updated");
    } catch {
      toast.error("Could not update language");
    }
  };

  const [businessName, setBusinessName] = useState(account?.business_name || "");
  const [savingBiz, setSavingBiz] = useState(false);

  const [members, setMembers] = useState([]);
  const [inviteToken, setInviteToken] = useState(null);
  const [inviteBusy, setInviteBusy] = useState(false);
  const [removeTarget, setRemoveTarget] = useState(null);

  const [retention, setRetention] = useState("never");
  const [customDays, setCustomDays] = useState(90);
  const [savingSettings, setSavingSettings] = useState(false);

  const [currentPw, setCurrentPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [changingPw, setChangingPw] = useState(false);

  const [displayName, setDisplayName] = useState(user?.name || "");
  const [savingName, setSavingName] = useState(false);

  const [leaveOpen, setLeaveOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deletePw, setDeletePw] = useState("");
  const [dangerBusy, setDangerBusy] = useState(false);

  const inviteUrl = inviteToken ? `${window.location.origin}/join/${inviteToken}` : "";

  const loadMembers = () => api.listAccountUsers().then(setMembers).catch(() => {});

  useEffect(() => {
    setBusinessName(account?.business_name || "");
  }, [account]);

  useEffect(() => {
    setDisplayName(user?.name || "");
  }, [user]);

  const saveName = async () => {
    setSavingName(true);
    try {
      await updateProfile(displayName.trim());
      await loadMembers();
      toast.success("Name updated");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSavingName(false);
    }
  };

  const leaveBusiness = async () => {
    setDangerBusy(true);
    try {
      await api.removeAccountUser(user.id);
      logout();
    } catch (e) {
      setDangerBusy(false);
      setLeaveOpen(false);
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const deleteBusiness = async () => {
    setDangerBusy(true);
    try {
      await api.deleteAccount(deletePw);
      logout();
    } catch (e) {
      setDangerBusy(false);
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not delete business");
    }
  };

  useEffect(() => {
    loadMembers();
    api.getInvitation().then((d) => setInviteToken(d.token)).catch(() => {});
    api
      .getSettings()
      .then((s) => {
        setRetention(s.retention);
        setCustomDays(s.custom_days);
      })
      .catch(() => {});
  }, []);

  const saveBusiness = async () => {
    const name = businessName.trim();
    if (!name) return toast.error("Business name is required");
    setSavingBiz(true);
    try {
      const acc = await api.updateAccount(name);
      setAccount(acc);
      toast.success("Business name updated");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSavingBiz(false);
    }
  };

  const generateInvite = async () => {
    setInviteBusy(true);
    try {
      const d = await api.generateInvitation();
      setInviteToken(d.token);
      toast.success("Invitation link ready");
    } catch {
      toast.error("Could not generate link");
    } finally {
      setInviteBusy(false);
    }
  };

  const revokeInvite = async () => {
    setInviteBusy(true);
    try {
      await api.revokeInvitation();
      setInviteToken(null);
      toast.success("Invitation link revoked");
    } catch {
      toast.error("Could not revoke link");
    } finally {
      setInviteBusy(false);
    }
  };

  const copyInvite = async () => {
    try {
      await navigator.clipboard.writeText(inviteUrl);
      toast.success("Link copied");
    } catch {
      toast.error("Copy failed — select and copy manually");
    }
  };

  const confirmRemove = async () => {
    try {
      const res = await api.removeAccountUser(removeTarget.id);
      setRemoveTarget(null);
      if (res.removed_self) {
        logout();
        return;
      }
      await loadMembers();
      toast.success("User removed");
    } catch (e) {
      setRemoveTarget(null);
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      await api.updateSettings({
        retention,
        custom_days: Math.max(1, parseInt(customDays, 10) || 1),
      });
      toast.success("Settings saved");
    } catch {
      toast.error("Could not save settings");
    } finally {
      setSavingSettings(false);
    }
  };

  const changePassword = async () => {
    if (newPw.length < 6) return toast.error("New password must be at least 6 characters");
    if (newPw !== confirmPw) return toast.error("New passwords do not match");
    setChangingPw(true);
    try {
      await api.changePassword(currentPw, newPw);
      setCurrentPw("");
      setNewPw("");
      setConfirmPw("");
      toast.success("Password changed");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setChangingPw(false);
    }
  };

  return (
    <div className="space-y-5">
      <div>
        <h1 className="font-display text-3xl font-extrabold tracking-tight text-foreground">
          {t("nav_settings")}
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          {t("settings_subtitle")}
        </p>
      </div>

      {/* Business info */}
      <Card className="rounded-2xl border-border p-6">
        <SectionLabel icon={Building2}>{t("business")}</SectionLabel>
        <Label htmlFor="biz" className="text-sm font-semibold">{t("business_name_label")}</Label>
        <div className="mt-1.5 flex gap-3">
          <Input
            id="biz"
            value={businessName}
            onChange={(e) => setBusinessName(e.target.value)}
            data-testid="business-name-input"
            className="h-12 flex-1 text-base"
          />
          <Button onClick={saveBusiness} disabled={savingBiz} data-testid="save-business-btn" className="h-12 rounded-xl px-5">
            {savingBiz ? <Loader2 className="h-5 w-5 animate-spin" /> : <Save className="h-5 w-5" />}
          </Button>
        </div>
        <p className="mt-2 text-xs text-muted-foreground">
          {t("shared_everyone")}
        </p>
      </Card>

      {/* Invite */}
      <Card className="rounded-2xl border-border p-6">
        <SectionLabel icon={Link2}>{t("invite_users")}</SectionLabel>
        {inviteToken ? (
          <>
            <div className="flex gap-2">
              <Input
                readOnly
                value={inviteUrl}
                data-testid="invite-link-input"
                onFocus={(e) => e.target.select()}
                className="h-12 flex-1 text-sm"
              />
              <Button variant="outline" onClick={copyInvite} data-testid="copy-invite-btn" className="h-12 rounded-xl px-4">
                <Copy className="h-5 w-5" />
              </Button>
            </div>
            <p className="mt-2 text-xs text-muted-foreground">
              {t("invite_anyone_join")} {account?.business_name}.
            </p>
            <div className="mt-4 flex flex-wrap gap-2">
              <Button variant="outline" onClick={generateInvite} disabled={inviteBusy} data-testid="regenerate-invite-btn" className="h-11 rounded-xl">
                <RefreshCw className="mr-2 h-4 w-4" /> {t("regenerate")}
              </Button>
              <Button variant="outline" onClick={revokeInvite} disabled={inviteBusy} data-testid="revoke-invite-btn" className="h-11 rounded-xl text-rose-600 hover:text-rose-700">
                <Ban className="mr-2 h-4 w-4" /> {t("revoke")}
              </Button>
            </div>
          </>
        ) : (
          <>
            <p className="text-sm text-muted-foreground">
              {t("invite_generate_desc")} {account?.business_name}.
            </p>
            <Button onClick={generateInvite} disabled={inviteBusy} data-testid="generate-invite-btn" className="mt-4 h-12 w-full rounded-xl font-bold">
              {inviteBusy ? <Loader2 className="mr-2 h-5 w-5 animate-spin" /> : <Link2 className="mr-2 h-5 w-5" />}
              {t("generate_invite")}
            </Button>
          </>
        )}
      </Card>

      {/* Team */}
      <Card className="rounded-2xl border-border p-6">
        <SectionLabel icon={Users}>{t("team")} ({members.length})</SectionLabel>
        <div className="space-y-2" data-testid="team-list">
          {members.map((m) => (
            <div key={m.id} className="flex items-center justify-between rounded-xl border border-border p-3">
              <div className="min-w-0">
                <p className="font-semibold text-foreground truncate">
                  {m.name ? m.name : m.email}
                  {m.id === user.id && (
                    <span className="ml-2 rounded-full bg-primary/10 px-2 py-0.5 text-xs font-bold text-primary">{t("you_badge")}</span>
                  )}
                </p>
                {m.name && <p className="text-xs text-muted-foreground truncate">{m.email}</p>}
              </div>
              <Button
                variant="ghost"
                size="icon"
                className="h-10 w-10 shrink-0 text-muted-foreground hover:text-rose-600"
                onClick={() => setRemoveTarget(m)}
                data-testid={`remove-user-${m.id}`}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          ))}
        </div>
      </Card>

      {/* Retention */}
      <Card className="rounded-2xl border-border p-6">
        <SectionLabel>{t("record_retention")}</SectionLabel>
        <RadioGroup value={retention} onValueChange={setRetention} className="space-y-3">
          {retentionOptions.map((o) => (
            <label
              key={o.value}
              htmlFor={`retention-${o.value}`}
              data-testid={`retention-${o.value}`}
              className={`flex cursor-pointer items-start gap-3 rounded-xl border p-4 transition-colors duration-200 ${
                retention === o.value ? "border-primary bg-primary/5" : "border-border hover:bg-secondary"
              }`}
            >
              <RadioGroupItem value={o.value} id={`retention-${o.value}`} className="mt-1" />
              <div>
                <p className="font-semibold text-foreground">{t(o.labelKey)}</p>
                <p className="text-sm text-muted-foreground">{t(o.descKey)}</p>
              </div>
            </label>
          ))}
        </RadioGroup>
        {retention === "custom" && (
          <div className="mt-4 flex items-center gap-3">
            <Label htmlFor="custom-days" className="text-sm font-semibold">{t("delete_after")}</Label>
            <Input
              id="custom-days"
              type="number"
              inputMode="numeric"
              min="1"
              value={customDays}
              onChange={(e) => setCustomDays(e.target.value)}
              data-testid="custom-days-input"
              className="h-11 w-24 text-center tabular-nums"
            />
            <span className="text-sm text-muted-foreground">{t("days")}</span>
          </div>
        )}
        <Button onClick={saveSettings} disabled={savingSettings} data-testid="save-settings-btn" className="mt-6 h-12 w-full rounded-xl font-bold active:scale-[0.98]">
          {savingSettings ? <Loader2 className="mr-2 h-5 w-5 animate-spin" /> : <Save className="mr-2 h-5 w-5" />}
          {t("save_settings")}
        </Button>
      </Card>

      {/* Language */}
      <Card className="rounded-2xl border-border p-6">
        <SectionLabel icon={Languages}>{t("language")}</SectionLabel>
        <select
          value={user.language || "en"}
          onChange={(e) => changeLanguage(e.target.value)}
          data-testid="settings-language-select"
          className="h-12 w-full rounded-md border border-input bg-background px-3 text-base"
        >
          <option value="en">English</option>
          <option value="es">Español</option>
        </select>
        <p className="mt-2 text-xs text-muted-foreground">{t("lang_applies_note")}</p>
      </Card>

      {/* Change password */}
      <Card className="rounded-2xl border-border p-6">
        <SectionLabel icon={KeyRound}>{t("change_password")}</SectionLabel>
        <div className="space-y-3">
          <div>
            <Label htmlFor="current-pw" className="text-sm font-semibold">{t("current_password")}</Label>
            <Input
              id="current-pw"
              type="password"
              value={currentPw}
              onChange={(e) => setCurrentPw(e.target.value)}
              data-testid="current-password-input"
              className="mt-1.5 h-12"
            />
          </div>
          <div>
            <Label htmlFor="new-pw" className="text-sm font-semibold">{t("new_password")}</Label>
            <Input
              id="new-pw"
              type="password"
              value={newPw}
              onChange={(e) => setNewPw(e.target.value)}
              data-testid="new-password-input"
              placeholder={t("at_least_6")}
              className="mt-1.5 h-12"
            />
          </div>
          <div>
            <Label htmlFor="confirm-pw" className="text-sm font-semibold">{t("confirm_new_password")}</Label>
            <Input
              id="confirm-pw"
              type="password"
              value={confirmPw}
              onChange={(e) => setConfirmPw(e.target.value)}
              data-testid="confirm-password-input"
              className="mt-1.5 h-12"
            />
          </div>
        </div>
        <Button
          onClick={changePassword}
          disabled={changingPw || !currentPw || !newPw || !confirmPw}
          data-testid="change-password-btn"
          className="mt-5 h-12 w-full rounded-xl font-bold active:scale-[0.98]"
        >
          {changingPw ? <Loader2 className="mr-2 h-5 w-5 animate-spin" /> : <KeyRound className="mr-2 h-5 w-5" />}
          {t("update_password")}
        </Button>
      </Card>

      {/* Profile / account / logout */}
      <Card className="rounded-2xl border-border p-6">
        <SectionLabel icon={UserCircle}>{t("your_profile")}</SectionLabel>
        <Label htmlFor="display-name" className="text-sm font-semibold">{t("your_name")}</Label>
        <div className="mt-1.5 flex gap-3">
          <Input
            id="display-name"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder={t("add_your_name")}
            data-testid="display-name-input"
            className="h-12 flex-1 text-base"
          />
          <Button onClick={saveName} disabled={savingName} data-testid="save-name-btn" className="h-12 rounded-xl px-5">
            {savingName ? <Loader2 className="h-5 w-5 animate-spin" /> : <Save className="h-5 w-5" />}
          </Button>
        </div>
        <p className="mt-2 text-xs text-muted-foreground">
          {t("profile_name_note")}
        </p>

        <div className="mt-5 border-t border-border pt-4">
          <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground">{t("signed_in_as")}</p>
          <p className="mt-1 font-semibold text-foreground" data-testid="current-user-email">{user.email}</p>
        </div>
        <Button variant="outline" onClick={logout} data-testid="logout-btn" className="mt-4 h-12 w-full rounded-xl font-bold text-rose-600 hover:text-rose-700">
          <LogOut className="mr-2 h-5 w-5" /> {t("log_out")}
        </Button>
      </Card>

      {/* Danger zone */}
      <Card className="rounded-2xl border-2 border-rose-200 bg-rose-50/40 p-6">
        <div className="mb-4 flex items-center gap-2">
          <AlertTriangle className="h-4 w-4 text-rose-600" />
          <p className="text-xs font-bold uppercase tracking-widest text-rose-600">{t("danger_zone")}</p>
        </div>
        <div className="space-y-3">
          {members.length > 1 && (
            <div className="flex flex-col gap-2 rounded-xl border border-border bg-card p-4 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <p className="font-semibold text-foreground">{t("leave_business")}</p>
                <p className="text-sm text-muted-foreground">
                  {t("leave_business_desc")}
                </p>
              </div>
              <Button
                variant="outline"
                onClick={() => setLeaveOpen(true)}
                data-testid="leave-business-btn"
                className="h-11 shrink-0 rounded-xl font-bold text-rose-600 hover:text-rose-700"
              >
                <DoorOpen className="mr-2 h-4 w-4" /> {t("leave")}
              </Button>
            </div>
          )}
          <div className="flex flex-col gap-2 rounded-xl border border-border bg-card p-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="font-semibold text-foreground">{t("delete_business")}</p>
              <p className="text-sm text-muted-foreground">
                {t("delete_business_desc_pre")} {account?.business_name}{t("delete_business_desc_post")}
              </p>
            </div>
            <Button
              variant="outline"
              onClick={() => setDeleteOpen(true)}
              data-testid="delete-business-btn"
              className="h-11 shrink-0 rounded-xl font-bold text-rose-600 hover:text-rose-700"
            >
              <Trash2 className="mr-2 h-4 w-4" /> {t("delete")}
            </Button>
          </div>
        </div>
      </Card>

      <AlertDialog open={!!removeTarget} onOpenChange={(o) => !o && setRemoveTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t("remove")} {removeTarget?.name || removeTarget?.email}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              {removeTarget?.id === user.id
                ? t("remove_self_desc")
                : t("remove_other_desc")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={confirmRemove} data-testid="confirm-remove-user-btn">
              {t("remove")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={leaveOpen} onOpenChange={(o) => !o && !dangerBusy && setLeaveOpen(false)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("leave_q_pre")} {account?.business_name}?</AlertDialogTitle>
            <AlertDialogDescription>
              {t("leave_confirm_desc")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={dangerBusy}>{t("cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={leaveBusiness} disabled={dangerBusy} data-testid="confirm-leave-business-btn">
              {t("leave")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={deleteOpen} onOpenChange={(o) => { if (!o && !dangerBusy) { setDeleteOpen(false); setDeletePw(""); } }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("delete_q_pre")} {account?.business_name}?</AlertDialogTitle>
            <AlertDialogDescription>
              {t("delete_confirm_desc")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <Input
            type="password"
            value={deletePw}
            onChange={(e) => setDeletePw(e.target.value)}
            placeholder={t("your_password")}
            data-testid="delete-business-password-input"
            className="h-12"
          />
          <AlertDialogFooter>
            <AlertDialogCancel disabled={dangerBusy}>{t("cancel")}</AlertDialogCancel>
            <Button
              onClick={deleteBusiness}
              disabled={dangerBusy || !deletePw}
              data-testid="confirm-delete-business-btn"
              className="h-10 rounded-lg bg-rose-600 font-bold text-white hover:bg-rose-700"
            >
              {dangerBusy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              {t("delete_business_btn")}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
